// domain/constants/targetDataType.js

/**
 * How a target is measured.
 *
 * The progress-counting facts, plus what an import has to judge a file by: which program types
 * accept each data type, which metrics each one allows, and which program metrics each inherits. The
 * rest (labels, timer settings) stays in `src/constants/targets` until a bet needs it, per the
 * convention that a context models what it needs today and grows.
 *
 * THESE LISTS ARE COPIES OF `src/constants/targets.TARGET_DATA_TYPE_DEFINITIONS`, and deliberately
 * so: that file imports `boot/i18n`, and nothing reachable from a context's `index.js` may import app
 * code. They are small, they change when the product's data types change, and a disagreement shows
 * up as an import refusing something the UI offers.
 *
 * @domainObject value-object
 * @invariant A target's data type must be one the program type accepts. It is a rule about the
 *   pair, so it is stated here and applied by whoever holds both: `isDataTypeAllowedInProgramType`
 *   below, called by the importer once it knows a target's program.
 * @typedef {'discreteTrial'|'duration'|'rate'|'taskAnalysis'|'targetSet'|'shaping'
 *   |'randomRotation'|'cabBranch'|'instructions'|'interval'|'frequency'} TargetDataType
 */
import { MEASUREMENT_METRIC } from './measurementMetric';

export const TARGET_DATA_TYPE = {
  DISCRETE_TRIAL: 'discreteTrial',
  DURATION: 'duration',
  RATE: 'rate',
  TASK_ANALYSIS: 'taskAnalysis',
  TARGET_SET: 'targetSet',
  SHAPING: 'shaping',
  RANDOM_ROTATION: 'randomRotation',
  CAB_BRANCH: 'cabBranch',
  INSTRUCTIONS: 'instructions',
  INTERVAL: 'interval',
  FREQUENCY: 'frequency',
};

/**
 * Which data types are left out of a program's target counts, and why.
 *
 * `excludedFromProgressCount` — the target is a container rather than a unit of work, so counting
 * it as well as its children would double-count the program.
 *
 * `excludesSubTargetsFromProgressCount` — the inverse: the PARENT counts and its children do not.
 * Task analysis is the only one; its steps are parts of one skill, not separate skills.
 *
 * `hasSubTargets` — the type is allowed children at all. A target parented to one that is not is
 * an orphan: a leftover of an edit that changed a parent's type, counted nowhere, because the
 * program it belongs to no longer has a place for it.
 *
 * `selfNesting` — the type appears as both a container and a unit of work, so the exclusion has to
 * be decided per target rather than per type: a parent interval target is excluded, a nested one is
 * not. `interval` is the only such type, and the behaviour is inherited from
 * `src/utils/TargetUtils.shouldExcludeTargetByType`.
 *
 * `defaultMetric` — how this type is summarised when nobody has chosen. Absent for the container
 * types, which are summarised through their children rather than in their own right. It is here
 * rather than only in `src/constants/targets` because a context may not import that file, and deriving a
 * program's metric from the targets arriving with it is the first rule that needed to ask.
 *
 * `allowedProgramTypes` — the program types that accept this data type at all.
 *
 * `allowedMetrics` — the metrics this type may be measured by. ABSENT MEANS UNCONSTRAINED, not
 * "none": the container types place no restriction, because what they are summarised by is decided
 * by their children.
 *
 * `passThroughMetrics` — the program metrics a target of this type inherits when it has none of its
 * own; any other program metric gives it `defaultMetric` instead. Mirrors `src/constants/targets`
 * as `getTargetMetric` reads it, so an import decides inheritance the way every screen will.
 *
 * `has*` — the optional fields this type carries at all. ABSENT MEANS NO, unlike `allowedMetrics`:
 * a field a type does not have is a field, not a missing restriction. Only the ones an import can
 * set are listed, so this is shorter than the same flags in `src/constants/targets`.
 */
export const TARGET_DATA_TYPE_META = {
  [TARGET_DATA_TYPE.DISCRETE_TRIAL]: {
    excludedFromProgressCount: false,
    excludesSubTargetsFromProgressCount: false,
    hasSubTargets: false,
    defaultMetric: MEASUREMENT_METRIC.PERCENT_CORRECT,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'ptp', 'asr'],
    allowedMetrics: [MEASUREMENT_METRIC.PERCENT_CORRECT, MEASUREMENT_METRIC.FREQUENCY],
    passThroughMetrics: [MEASUREMENT_METRIC.PERCENT_CORRECT, MEASUREMENT_METRIC.FREQUENCY],
    hasMaintenanceInterval: true,
    hasPromptLevelTemplate: true,
    hasWorkflow: true,
  },
  [TARGET_DATA_TYPE.DURATION]: {
    excludedFromProgressCount: false,
    excludesSubTargetsFromProgressCount: false,
    hasSubTargets: false,
    defaultMetric: MEASUREMENT_METRIC.TOTAL_DURATION,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'ptp', 'asr'],
    allowedMetrics: [
      MEASUREMENT_METRIC.PERCENT_CORRECT,
      MEASUREMENT_METRIC.FREQUENCY,
      MEASUREMENT_METRIC.TOTAL_DURATION,
      MEASUREMENT_METRIC.MIN_DURATION,
      MEASUREMENT_METRIC.MAX_DURATION,
      MEASUREMENT_METRIC.AVG_DURATION,
    ],
    passThroughMetrics: [
      MEASUREMENT_METRIC.TOTAL_DURATION, MEASUREMENT_METRIC.MIN_DURATION,
      MEASUREMENT_METRIC.MAX_DURATION, MEASUREMENT_METRIC.AVG_DURATION,
    ],
    hasMaintenanceInterval: true,
    hasPromptLevelTemplate: true,
    hasWorkflow: true,
  },
  [TARGET_DATA_TYPE.RATE]: {
    excludedFromProgressCount: false,
    excludesSubTargetsFromProgressCount: false,
    hasSubTargets: false,
    defaultMetric: MEASUREMENT_METRIC.RATE_PER_HOUR,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'ptp', 'asr'],
    allowedMetrics: [
      MEASUREMENT_METRIC.PERCENT_CORRECT,
      MEASUREMENT_METRIC.FREQUENCY,
      MEASUREMENT_METRIC.RATE_PER_HOUR,
      MEASUREMENT_METRIC.RATE_PER_MINUTE,
    ],
    passThroughMetrics: [MEASUREMENT_METRIC.RATE_PER_HOUR, MEASUREMENT_METRIC.RATE_PER_MINUTE],
    hasMaintenanceInterval: true,
    hasPromptLevelTemplate: true,
    hasWorkflow: true,
  },
  [TARGET_DATA_TYPE.FREQUENCY]: {
    excludedFromProgressCount: false,
    excludesSubTargetsFromProgressCount: false,
    hasSubTargets: false,
    defaultMetric: MEASUREMENT_METRIC.FREQUENCY,
    allowedProgramTypes: ['simple', 'behaviorTracking'],
    allowedMetrics: [MEASUREMENT_METRIC.FREQUENCY],
    passThroughMetrics: [MEASUREMENT_METRIC.FREQUENCY],
    hasMaintenanceInterval: true,
    hasPromptLevelTemplate: true,
    hasWorkflow: true,
  },
  [TARGET_DATA_TYPE.TASK_ANALYSIS]: {
    excludedFromProgressCount: false,
    excludesSubTargetsFromProgressCount: true,
    hasSubTargets: true,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'asr'],
    hasMaintenanceInterval: true,
    hasPromptLevelTemplate: true,
  },
  [TARGET_DATA_TYPE.TARGET_SET]: {
    excludedFromProgressCount: true,
    excludesSubTargetsFromProgressCount: false,
    hasSubTargets: true,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'ptp', 'asr'],
    hasPromptLevelTemplate: true,
  },
  [TARGET_DATA_TYPE.SHAPING]: {
    excludedFromProgressCount: true,
    excludesSubTargetsFromProgressCount: false,
    hasSubTargets: true,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'asr'],
    hasPromptLevelTemplate: true,
    hasWorkflow: true,
  },
  [TARGET_DATA_TYPE.RANDOM_ROTATION]: {
    excludedFromProgressCount: true,
    excludesSubTargetsFromProgressCount: false,
    hasSubTargets: true,
    allowedProgramTypes: ['sbt'],
    hasPromptLevelTemplate: true,
    hasWorkflow: true,
  },
  [TARGET_DATA_TYPE.CAB_BRANCH]: {
    excludedFromProgressCount: true,
    excludesSubTargetsFromProgressCount: false,
    hasSubTargets: true,
    allowedProgramTypes: ['sbt'],
  },
  [TARGET_DATA_TYPE.INSTRUCTIONS]: {
    excludedFromProgressCount: true,
    excludesSubTargetsFromProgressCount: false,
    hasSubTargets: false,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'ptp', 'asr'],
    hasInstructions: true,
  },
  [TARGET_DATA_TYPE.INTERVAL]: {
    excludedFromProgressCount: true,
    excludesSubTargetsFromProgressCount: false,
    hasSubTargets: true,
    selfNesting: true,
    defaultMetric: MEASUREMENT_METRIC.PERCENT_CORRECT,
    allowedProgramTypes: ['simple', 'behaviorTracking'],
    allowedMetrics: [MEASUREMENT_METRIC.PERCENT_CORRECT, MEASUREMENT_METRIC.FREQUENCY],
    passThroughMetrics: [MEASUREMENT_METRIC.PERCENT_CORRECT, MEASUREMENT_METRIC.FREQUENCY],
    hasPromptLevelTemplate: true,
  },
};

/**
 * Should this target be left out of its program's counts on the strength of its data type alone?
 *
 * @param {{ dataType?: string, parentTargetId?: string|null }} target
 * @returns {boolean}
 */
export function isExcludedFromProgressCount(target) {
  const meta = TARGET_DATA_TYPE_META[target?.dataType];
  if (!meta) {
    // An unknown or absent data type counts. A target with no type is still a target somebody
    // added, and silently dropping it understates the program.
    return false;
  }
  if (meta.selfNesting === true) {
    return !target.parentTargetId;
  }
  return meta.excludedFromProgressCount;
} // isExcludedFromProgressCount()

/**
 * May a target of this data type hold sub-targets at all?
 *
 * Asked by the importer, which refuses a file that gives steps to a type with nowhere to put them,
 * and by the count, which does not count the children of one that already has them.
 *
 * @param {string} dataType
 * @returns {boolean}
 */
export function canHaveSubTargets(dataType) {
  return TARGET_DATA_TYPE_META[dataType]?.hasSubTargets === true;
} // canHaveSubTargets()

/**
 * May a target of this data type live in a program of this type?
 *
 * An unknown data type is allowed through. Refusing it here would report the same row twice, once
 * as an unrecognised type by its own field rule and again as a mismatch, and the second message
 * would be the less useful of the two.
 *
 * @param {TargetDataType} dataType
 * @param {string} programType
 * @returns {boolean}
 */
export function isDataTypeAllowedInProgramType(dataType, programType) {
  const allowed = TARGET_DATA_TYPE_META[dataType]?.allowedProgramTypes;
  if (!allowed || !programType) {
    return true;
  }
  return allowed.includes(programType);
} // isDataTypeAllowedInProgramType()

/**
 * May a target of this data type be measured by this metric?
 *
 * A type with no `allowedMetrics` places no constraint, which is the container types: what a set of
 * targets is summarised by follows from its children, not from itself.
 *
 * @param {TargetDataType} dataType
 * @param {string} metric
 * @returns {boolean}
 */
export function isMetricAllowedForDataType(dataType, metric) {
  const allowed = TARGET_DATA_TYPE_META[dataType]?.allowedMetrics;
  if (!allowed || !metric) {
    return true;
  }
  return allowed.includes(metric);
} // isMetricAllowedForDataType()

/**
 * Can a target of this data type actually be measured by this metric?
 *
 * Stricter than `isMetricAllowedForDataType`: a container type measures nothing itself, so it
 * measures no metric rather than accepting any.
 *
 * @param {TargetDataType} dataType
 * @param {string} metric
 * @returns {boolean}
 */
export function measuresMetric(dataType, metric) {
  return TARGET_DATA_TYPE_META[dataType]?.allowedMetrics?.includes(metric) === true;
} // measuresMetric()

/**
 * Fields a target only has on some data types, and the flag that says which.
 *
 * Keyed by the field an import can set, so a caller asks about a field rather than about a flag.
 */
const OPTIONAL_TARGET_FIELDS = {
  workflow: 'hasWorkflow',
  promptLevelTemplate: 'hasPromptLevelTemplate',
  instructions: 'hasInstructions',
  maintenanceInterval: 'hasMaintenanceInterval',
};

/**
 * Does a target of this data type have this field at all?
 *
 * A field nobody has claimed is optional belongs to every type, which is most of them: label,
 * status and the lifecycle dates are not listed above and are not meant to be.
 *
 * An unknown data type has every field, for the same reason it accepts every program type: its own
 * rule already calls it unrecognised, and a second message about a field would bury the first.
 *
 * @param {TargetDataType} dataType
 * @param {string} field
 * @returns {boolean}
 */
export function dataTypeHasField(dataType, field) {
  const flag = OPTIONAL_TARGET_FIELDS[field];
  const meta = TARGET_DATA_TYPE_META[dataType];
  if (!flag || !meta) {
    return true;
  }
  return meta[flag] === true;
} // dataTypeHasField()
